import React from "react";
import api from "../api/client";
import PdfViewer from "../components/PdfViewer";
import { useOrg } from "../context/OrgContext";
import { useTerminology } from "../context/TerminologyContext";
import { withOrgQS } from "../utils/url";
import { API_BASE } from "../config";
import {
  DateRangePicker,
  formatChecklistMonth,
  isChecklistMonthLabel,
  LotSelectionTable,
} from "../components/ui-helpers";
import { formatDateTime } from "../utils/datetime";
import { downloadText } from "../utils/download";

const emptyChecklistArtifact = {
  checklist_exists: false,
  pdf_exists: false,
  json_exists: false,
  mode: "current",
};

const cleanValue = (value) => String(value ?? "").trim();
const retrieveLotRowId = (row) =>
  [
    cleanValue(row?.vendor),
    cleanValue(row?.material_id) ||
      `legacy:${cleanValue(row?.material)}:${cleanValue(row?.sku)}`,
    cleanValue(row?.lot_no),
  ].join("::");

function isLocalDateInRange(value, range) {
  const raw = String(value || "").slice(0, 10);
  if (!raw) return true;
  if (range?.date_from && raw < range.date_from) return false;
  if (range?.date_to && raw > range.date_to) return false;
  return true;
}

function downloadJson(blob, filename) {
  downloadText(
    filename,
    JSON.stringify(blob || {}, null, 2),
    "application/json;charset=utf-8;",
  );
}

function checklistJsonForDisplay(blob) {
  if (!blob || typeof blob !== "object" || Array.isArray(blob)) return blob;
  const header = blob.header;
  if (!header || typeof header !== "object" || Array.isArray(header))
    return blob;
  return {
    ...blob,
    header: Object.fromEntries(
      Object.entries(header).map(([label, value]) => [
        label,
        isChecklistMonthLabel(label) ? formatChecklistMonth(value) : value,
      ]),
    ),
  };
}

export default function RetrieveCoaPage() {
  const { current: org } = useOrg();
  const { t, tp } = useTerminology();

  const [lotRows, setLotRows] = React.useState([]);
  const [loadingLots, setLoadingLots] = React.useState(false);
  const [lotError, setLotError] = React.useState("");
  const [selectedRowId, setSelectedRowId] = React.useState("");
  const [vendor, setVendor] = React.useState("");
  const [material, setMaterial] = React.useState("");
  const [sku, setSku] = React.useState("");
  const [materialId, setMaterialId] = React.useState("");
  const [lot, setLot] = React.useState("");
  const [pdfUrl, setPdfUrl] = React.useState("");

  const [versions, setVersions] = React.useState([]);
  const [ver, setVer] = React.useState("");
  const [includeChecklist, setIncludeChecklist] = React.useState(false);
  const [chkUrl, setChkUrl] = React.useState("");
  const [checklistJson, setChecklistJson] = React.useState(null);
  const [checklistArtifact, setChecklistArtifact] = React.useState(
    emptyChecklistArtifact,
  );
  const [lotTableOpen, setLotTableOpen] = React.useState(true);
  const [lotStatusFilter, setLotStatusFilter] = React.useState("All");
  const [dateDraft, setDateDraft] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [dateRange, setDateRange] = React.useState({
    date_from: "",
    date_to: "",
  });

  const selectedRow = React.useMemo(
    () =>
      lotRows.find((row) => retrieveLotRowId(row) === selectedRowId) || null,
    [lotRows, selectedRowId],
  );

  const resetViewerState = React.useCallback(() => {
    setPdfUrl("");
    setChkUrl("");
    setChecklistJson(null);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoadingLots(true);
    setLotError("");
    api
      .get("/user/retrieve_lots")
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data?.items) ? r.data.items : [];
        setLotRows(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setLotRows([]);
        setLotError(
          err?.response?.data?.error ||
            `Could not load retrievable COA ${tp("lot").toLowerCase()}.`,
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingLots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [org, tp]);

  React.useEffect(() => {
    if (!selectedRowId) return;
    const stillPresent = lotRows.some(
      (row) => retrieveLotRowId(row) === selectedRowId,
    );
    if (!stillPresent) {
      setSelectedRowId("");
      setVendor("");
      setMaterial("");
      setSku("");
      setMaterialId("");
      setLot("");
      setVersions([]);
      setVer("");
      setIncludeChecklist(false);
      setChecklistArtifact(emptyChecklistArtifact);
      setLotTableOpen(true);
      resetViewerState();
    }
  }, [lotRows, selectedRowId, resetViewerState]);

  const selectLotRow = React.useCallback(
    (row) => {
      setSelectedRowId(retrieveLotRowId(row));
      setVendor(cleanValue(row?.vendor));
      setMaterial(cleanValue(row?.material));
      setSku(cleanValue(row?.sku));
      setMaterialId(cleanValue(row?.material_id));
      setLot(cleanValue(row?.lot_no));
      setVersions([]);
      setVer("");
      setIncludeChecklist(false);
      setChecklistArtifact(emptyChecklistArtifact);
      resetViewerState();
    },
    [resetViewerState],
  );

  React.useEffect(() => {
    setVersions([]);
    setVer("");
    resetViewerState();
    if (!vendor || !sku || !lot) return;
    api
      .get("/invalidated/versions", {
        params: {
          vendor,
          sku,
          lot_no: lot,
          ...(materialId ? { material_id: materialId } : {}),
        },
      })
      .then((r) => setVersions(r.data.versions || []))
      .catch(() => setVersions([]));
  }, [vendor, sku, materialId, lot, resetViewerState]);

  React.useEffect(() => {
    if (
      selectedRow &&
      !selectedRow.has_current_pdf &&
      versions.length &&
      !ver
    ) {
      setVer(versions[0]);
    }
  }, [selectedRow, versions, ver]);

  React.useEffect(() => {
    setIncludeChecklist(false);
    setChkUrl("");
    setChecklistJson(null);
    setChecklistArtifact(emptyChecklistArtifact);
    if (!vendor || !sku || !lot) return;
    const params = {
      vendor,
      sku,
      lot_no: lot,
      ...(materialId ? { material_id: materialId } : {}),
    };
    if (ver) params.ts = ver;
    api
      .get("/user/checklist_artifact_status", { params })
      .then((r) => {
        setChecklistArtifact(r.data || emptyChecklistArtifact);
        setLotTableOpen(false);
      })
      .catch(() => setChecklistArtifact(emptyChecklistArtifact));
  }, [vendor, sku, materialId, lot, ver, org]);

  const view = async () => {
    if (!vendor || !sku || !lot) return;
    setChkUrl("");
    setChecklistJson(null);

    if (ver) {
      const p = `${API_BASE}/user/get_invalidated_pdf?${new URLSearchParams({ vendor, sku, lot_no: lot, ts: ver, ...(materialId ? { material_id: materialId } : {}) }).toString()}`;
      setPdfUrl(withOrgQS(p, org));
      if (includeChecklist && checklistArtifact.pdf_exists) {
        const c = `${API_BASE}/user/get_invalidated_checklist_pdf?${new URLSearchParams({ vendor, sku, lot_no: lot, ts: ver, ...(materialId ? { material_id: materialId } : {}) }).toString()}`;
        setChkUrl(withOrgQS(c, org));
      } else if (includeChecklist && checklistArtifact.json_exists) {
        const r = await api.get("/user/get_invalidated_checklist_json", {
          params: {
            vendor,
            sku,
            lot_no: lot,
            ts: ver,
            ...(materialId ? { material_id: materialId } : {}),
          },
        });
        setChecklistJson(r.data || {});
      }
    } else {
      const p = `${API_BASE}/user/get_processed_pdf?${new URLSearchParams({ vendor, sku, lot_no: lot, ...(materialId ? { material_id: materialId } : {}) }).toString()}`;
      setPdfUrl(withOrgQS(p, org));
      if (includeChecklist && checklistArtifact.pdf_exists) {
        const c = `${API_BASE}/user/get_checklist_pdf?${new URLSearchParams({ vendor, sku, lot_no: lot, ...(materialId ? { material_id: materialId } : {}) }).toString()}`;
        setChkUrl(withOrgQS(c, org));
      } else if (includeChecklist && checklistArtifact.json_exists) {
        const r = await api.get("/user/get_checklist_json", {
          params: {
            vendor,
            sku,
            lot_no: lot,
            ...(materialId ? { material_id: materialId } : {}),
          },
        });
        setChecklistJson(r.data || {});
      }
    }
  };

  const lotColumns = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
      { key: "lot_no", label: t("lot") },
      {
        key: "status",
        label: "Status",
        render: (row) => row.status || "Approved",
      },
      {
        key: "last_action_time",
        label: "Last Action",
        value: (row) => row.last_action_time || row.upload_time || "",
        render: (row) =>
          formatDateTime(row.last_action_time || row.upload_time),
      },
    ],
    [t],
  );

  const lotSortOptions = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
      { key: "lot_no", label: t("lot") },
      { key: "status", label: "Status" },
      {
        key: "last_action_time",
        label: "Last Action",
        value: (row) => row.last_action_time || row.upload_time || "",
      },
    ],
    [t],
  );

  const filteredLotRows = React.useMemo(() => {
    return lotRows.filter(
      (row) =>
        (lotStatusFilter === "All" ||
          cleanValue(row.status || "Approved").toLowerCase() ===
            lotStatusFilter.toLowerCase()) &&
        isLocalDateInRange(
          row.last_action_time || row.upload_time,
          dateRange,
        ),
    );
  }, [lotRows, lotStatusFilter, dateRange]);

  const lotEmptyText =
    lotStatusFilter === "All"
      ? loadingLots
        ? `Loading COA ${tp("lot").toLowerCase()}...`
        : `No retrievable COA ${tp("lot").toLowerCase()} found.`
      : `No ${lotStatusFilter} ${tp("lot").toLowerCase()} found.`;

  const retrieveToolbarControls = (
    <>
      <label>
        <span>Status</span>
        <select
          value={lotStatusFilter}
          onChange={(event) => setLotStatusFilter(event.target.value)}
        >
          <option value="All">All</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
          <option value="Invalidated">Invalidated</option>
          <option value="Initiated">Initiated</option>
          <option value="Pending">Pending</option>
        </select>
      </label>
      <DateRangePicker
        value={dateDraft}
        onChange={setDateDraft}
        onApply={() => setDateRange({ ...dateDraft })}
        onClear={(empty) => setDateRange(empty)}
        label="Last action range"
      />
    </>
  );

  const checklistFileName = `${lot || "lot"}${ver ? `-${ver}` : ""}-checklist.json`;
  const checklistPreviewJson = React.useMemo(
    () => checklistJsonForDisplay(checklistJson),
    [checklistJson],
  );

  return (
    <div className="section">
      <h2>Retrieve COAs</h2>
      <details
        className="selection-expander"
        open={lotTableOpen}
        onToggle={(event) => setLotTableOpen(event.currentTarget.open)}
      >
        <summary>Select COA {t("lot").toLowerCase()}</summary>
        <div className="helper-text" style={{ marginBottom: 10 }}>
          Search and select a processed {t("lot").toLowerCase()}, then choose a
          current or invalidated version to view.
        </div>
        {lotError ? (
          <div className="helper-text error-text" style={{ marginBottom: 10 }}>
            {lotError}
          </div>
        ) : null}
        <LotSelectionTable
          rows={filteredLotRows}
          columns={lotColumns}
          sortOptions={lotSortOptions}
          toolbarExtras={retrieveToolbarControls}
          selected={selectedRowId}
          onRowClick={selectLotRow}
          getRowId={retrieveLotRowId}
          disabledRow={(row) => row?.can_retrieve === false}
          searchPlaceholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, or status`}
          emptyText={lotEmptyText}
          pageSize={10}
          className="retrieve-coa-lot-table"
        />
      </details>

      {selectedRow ? (
        <div className="section selected-lot-card" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>
            Selected COA {t("lot").toLowerCase()}
          </h3>
          <div className="selected-lot-summary">
            <div>
              <b>{t("vendor")}:</b> {vendor || "—"}
            </div>
            <div>
              <b>{t("material")}:</b> {material || "—"}
            </div>
            <div>
              <b>SKU:</b> {sku || "—"}
            </div>
            <div>
              <b>{t("lot")}:</b> {lot || "—"}
            </div>
          </div>
          <div
            className="hstack"
            style={{ marginTop: 12, flexWrap: "wrap", gap: 12 }}
          >
            <div>
              <label>Version</label>
              <select value={ver} onChange={(e) => setVer(e.target.value)}>
                <option value="">Current</option>
                {versions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            {checklistArtifact.checklist_exists ? (
              <label style={{ alignSelf: "end" }}>
                <input
                  type="checkbox"
                  checked={includeChecklist}
                  onChange={(e) => setIncludeChecklist(e.target.checked)}
                />
                &nbsp;Include checklist
              </label>
            ) : null}
            <button
              className="primary"
              type="button"
              onClick={view}
              disabled={selectedRow?.can_retrieve === false}
            >
              View
            </button>
          </div>
          {selectedRow?.can_retrieve === false ? (
            <div className="helper-text error-text" style={{ marginTop: 8 }}>
              This row is missing the current PDF and cannot be retrieved yet.
            </div>
          ) : null}
        </div>
      ) : (
        <div className="helper-text" style={{ marginTop: 12 }}>
          Select a COA {t("lot").toLowerCase()} from the table to view it.
        </div>
      )}

      {pdfUrl && (
        <div style={{ marginTop: 16 }}>
          <PdfViewer url={pdfUrl} />
        </div>
      )}
      {includeChecklist && (chkUrl || checklistJson) ? (
        <details style={{ marginTop: 16 }} open>
          <summary>
            <b>Checklist</b>
          </summary>
          {chkUrl ? (
            <div style={{ marginTop: 12 }}>
              <PdfViewer url={chkUrl} />
            </div>
          ) : (
            <div className="table--card" style={{ marginTop: 12, padding: 16 }}>
              <div
                className="hstack"
                style={{ justifyContent: "space-between", marginBottom: 12 }}
              >
                <strong>Checklist JSON</strong>
                <button
                  className="ghost"
                  type="button"
                  onClick={() => downloadJson(checklistJson, checklistFileName)}
                >
                  Download JSON
                </button>
              </div>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                {JSON.stringify(checklistPreviewJson, null, 2)}
              </pre>
            </div>
          )}
        </details>
      ) : null}
    </div>
  );
}
